import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { IAdminSymbol } from '../../api/admin';

interface Props {
	open: boolean;
	symbols: IAdminSymbol[];
	onClose: () => void;
}

interface SymbolField {
	key: Exclude<keyof IAdminSymbol, 'symbol_metadata'>;
	label: string;
}

interface MetadataRow {
	path: string;
	value: string;
}

const EMPTY_VALUE = '';

const SYMBOL_FIELDS: SymbolField[] = [
	{ key: 'dataset_symbol_id', label: 'Dataset Symbol ID' },
	{ key: 'dataset_name', label: 'Dataset Name' },
	{ key: 'dataset_id', label: 'Dataset ID' },
	{ key: 'ds_type', label: 'Type' },
	{ key: 'ds_symbol_1', label: 'Symbol 1' },
	{ key: 'ds_symbol_2', label: 'Symbol 2' },
	{ key: 'ds_col_1', label: 'Column 1' },
	{ key: 'ds_col_2', label: 'Column 2' },
	{ key: 'first_seen', label: 'First Seen' },
	{ key: 'last_seen', label: 'Last Seen' },
	{ key: 'dataset_symbol_batch_id', label: 'Dataset Symbol Batch ID' },
	{ key: 'dataset_symbol_updated_at', label: 'Symbol Updated' },
	{ key: 'company_name', label: 'Mapped Company' },
	{ key: 'company_symbol', label: 'Mapped Company Symbol' },
	{ key: 'company_bloomberg_ticker', label: 'Company Bloomberg Ticker' },
	{ key: 'company_id', label: 'Company ID' },
	{ key: 'parent_company_id', label: 'Parent Company ID' },
	{ key: 'parent_company_symbol', label: 'Parent Company Symbol' },
	{ key: 'parent_company_name', label: 'Parent Company Name' },
	{ key: 'is_active', label: 'Mapping Status' },
	{ key: 'start_date', label: 'Mapped Start' },
	{ key: 'end_date', label: 'Mapped End' },
	{ key: 'mapping_batch_id', label: 'Mapping Batch ID' },
	{ key: 'mapping_updated_at', label: 'Mapping Updated' },
];

function formatValue(value: unknown) {
	if (value == null || value === '') return EMPTY_VALUE;
	if (typeof value === 'boolean') return value ? 'true' : 'false';
	if (typeof value === 'object') return JSON.stringify(value);
	return String(value);
}

function formatMappingState(symbol: Pick<IAdminSymbol, 'company_id' | 'is_active'>) {
	if (symbol.company_id == null) return 'Unmapped';
	return symbol.is_active ? 'Active' : 'Inactive';
}


function formatMappedCompany(symbol: IAdminSymbol) {
	if (symbol.company_id == null) return EMPTY_VALUE;
	const mappedSymbol = symbol.company_symbol?.trim() || symbol.company_bloomberg_ticker?.trim();
	const companyName = symbol.company_name?.trim();
	const primary = mappedSymbol && companyName
		? `${mappedSymbol} | ${companyName}`
		: mappedSymbol || companyName || String(symbol.company_id);
	const parentName = symbol.parent_company_name?.trim();
	const parent = symbol.parent_company_id != null && parentName
		? ` (${parentName})`
		: '';
	return `${primary}${parent}`;
}

function formatSymbolField(
	symbol: IAdminSymbol,
	field: Exclude<keyof IAdminSymbol, 'symbol_metadata'>,
) {
	if (field === 'company_name') return formatMappedCompany(symbol);
	if (field === 'is_active') return formatMappingState(symbol);
	return formatValue(symbol[field]);
}


function flattenMetadata(value: unknown, path: string): MetadataRow[] {
	if (Array.isArray(value)) {
		if (value.length === 0) return [{ path, value: '[]' }];
		return value.flatMap((item, index) => flattenMetadata(item, `${path}[${index}]`));
	}
	if (value !== null && typeof value === 'object') {
		const entries = Object.entries(value as Record<string, unknown>);
		if (entries.length === 0) return [{ path, value: '{}' }];
		return entries.flatMap(([key, item]) => flattenMetadata(item, path ? `${path}.${key}` : key));
	}
	return [{ path, value: value === null ? 'null' : formatValue(value) }];
}

function DataTable({ rows }: {
	rows: Array<{ field: string; key: string; rawField?: string; value: string }>;
}) {
	return (
		<TableContainer>
			<Table
				size="small"
				sx={{
					tableLayout: 'fixed',
					'& th, & td': {
						border: '1px solid',
						borderColor: 'divider',
					},
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell sx={{ width: { xs: '42%', sm: 250 }, fontWeight: 700 }}>Field</TableCell>
						<TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row) => (
						<TableRow key={row.key}>
							<TableCell component="th" scope="row" sx={{ verticalAlign: 'top' }}>
								<Typography variant="body2" fontWeight={600}>{row.field}</Typography>
								{row.rawField && (
									<Typography variant="caption" color="text.secondary">
										{row.rawField}
									</Typography>
								)}
							</TableCell>
							<TableCell
								sx={{
									fontFamily: 'monospace',
									overflowWrap: 'anywhere',
									verticalAlign: 'top',
									whiteSpace: 'pre-wrap',
								}}
							>
								{row.value}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
}

function SymbolDetailsCard({ symbol, position, total }: {
	symbol: IAdminSymbol;
	position: number;
	total: number;
}) {
	const symbolRows = SYMBOL_FIELDS.map((field) => ({
		field: field.label,
		key: field.key,
		rawField: field.key,
		value: formatSymbolField(symbol, field.key),
	}));
	const metadataRows = symbol.symbol_metadata
		? flattenMetadata(symbol.symbol_metadata, '').map((row, index) => ({
			field: row.path || 'Value',
			key: `${row.path}-${index}`,
			value: row.value,
		}))
		: [];

	return (
		<Paper variant="outlined" sx={{ overflow: 'hidden' }}>
			<Stack
				direction="row"
				alignItems="center"
				spacing={1}
				sx={{ px: 2, py: 1.25, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}
			>
				<Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
					Dataset Symbol {symbol.dataset_symbol_id}
				</Typography>
				<Chip label={`${symbol.dataset_name} (${symbol.dataset_id})`} size="small" variant="outlined" />
				{total > 1 && <Chip label={`${position} of ${total}`} size="small" />}
			</Stack>
			<Stack spacing={2} sx={{ p: 2 }}>
				<Box>
					<Typography variant="subtitle2" sx={{ mb: 0.75 }}>Symbol fields</Typography>
					<DataTable rows={symbolRows} />
				</Box>
				<Box>
					<Typography variant="subtitle2" sx={{ mb: 0.75 }}>Symbol metadata</Typography>
					{metadataRows.length > 0 ? (
						<DataTable rows={metadataRows} />
					) : (
						<Typography variant="body2" color="text.secondary">No symbol metadata</Typography>
					)}
				</Box>
			</Stack>
		</Paper>
	);
}

export default function SymbolDetailsDialog({ open, symbols, onClose }: Props) {
	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			aria-labelledby="symbol-details-title"
			PaperProps={{ sx: { backgroundImage: 'none', maxHeight: '90vh' } }}
		>
			<DialogTitle component="div" id="symbol-details-title" sx={{ pr: 7 }}>
				<Typography variant="h6">Symbol details</Typography>
				<Typography variant="body2" color="text.secondary">
					{symbols.length === 1
						? `All fields for dataset symbol ${symbols[0].dataset_symbol_id}`
						: `${symbols.length} symbols map to this company and dataset`}
				</Typography>
				<IconButton
					aria-label="Close symbol details"
					onClick={onClose}
					sx={{ position: 'absolute', top: 10, right: 10 }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				<Stack spacing={2}>
					{symbols.map((symbol, index) => (
						<SymbolDetailsCard
							key={symbol.row_id}
							symbol={symbol}
							position={index + 1}
							total={symbols.length}
						/>
					))}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="contained" onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
}
